import { ActionIcon, Badge, Box, Button, Checkbox, Divider, EmptyState, Group, MultiSelect, Popover, Select, Stack, Switch, Text, TextInput, Tooltip } from "@mantine/core";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { IconArrowBack, IconArrowBackUp, IconColumns, IconDots, IconDownload, IconFilter, IconFilterPlus, IconGripVertical, IconSettings, IconSitemap, IconTrash, IconX } from "@tabler/icons-react";
import * as React from "react";
import type { ComponentProps, ReactNode } from "react";
import type { AppTableFeatures } from "./useAppTable";
import type { Column, ReactTable, RowData } from "@tanstack/react-table";
import { getFilterOperators, type ExtendedColumnFilter, type FilterOperator } from "./filters";
import { SortManager, type SortColumnOption } from "@ui/SortManager";
import { ResetToDefaultButton } from "@ui/ResetToDefaultButton";

type AppTable = ReactTable<AppTableFeatures, RowData, any>;
type AppColumn = Column<AppTableFeatures, RowData, any>;

export type AppTableControlProps<TData extends RowData = RowData> = {
  table: ReactTable<AppTableFeatures, TData, any>;
};

export type AppTableToolbarControlsOptions = {
    filter?: boolean;
    grouping?: boolean;
    sort?: boolean;
    view?: boolean;
    gap?: ComponentProps<typeof Group>["gap"];
    striped?: boolean | "odd" | "even";
    onStripedChange?: (striped: boolean | "odd" | "even") => void;
    compact?: boolean;
    onCompactChange?: (compact: boolean) => void;
    onResetToDefault?: () => void;
    onExportToExcel?: () => void | Promise<void>;
    moreOptions?: boolean;
};

function asAppTable<TData extends RowData>(table: ReactTable<AppTableFeatures, TData, any>) {
  return table as unknown as AppTable;
}

function getColumnLabel(column: AppColumn) {
  return column.columnDef.meta?.label ?? (typeof column.columnDef.header === "string" ? column.columnDef.header : column.id);
}

function getSortColumnValueType(column: AppColumn): SortColumnOption["valueType"] {
  if (column.columnDef.meta?.nameSorting) return "person";

  switch (column.columnDef.meta?.variant) {
    case "number":
      return "number";
    case "date":
      return "date";
    case "boolean":
      return "boolean";
    case "select":
    case "multi-select":
      return "category";
    case "text":
    default:
      return "string";
  }
}

function SortableFrame({ id, children }: { id: string; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{
        opacity: isDragging ? 0.6 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
        cursor: "grab",
      }}
    >
      {children}
    </Box>
  );
}

function toDateInputValue(value: unknown) {
  if (!value) return "";
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
}

function makeFilterId() {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function updateColumnFilters(
  table: AppTable,
  updater: ExtendedColumnFilter[] | ((current: ExtendedColumnFilter[]) => ExtendedColumnFilter[]),
) {
  table.setColumnFilters((current) => {
    const currentFilters = current as ExtendedColumnFilter[];
    return typeof updater === "function" ? updater(currentFilters) : updater;
  });
}

function FilterValueInput({
  column,
  filter,
  onFilterUpdate,
}: {
  column: AppColumn;
  filter: ExtendedColumnFilter;
  onFilterUpdate: (filterId: string, patch: Partial<ExtendedColumnFilter>) => void;
}) {
  if (!filter.filterId) return null;
  const variant = column.columnDef.meta?.variant ?? "text";
  const operator = filter.operator ?? "includesString";
  const disabled = operator === "isEmpty" || operator === "isNotEmpty";

  if (disabled) {
    return <Text c="dimmed">No value required</Text>;
  }

  if (variant === "select" || variant === "boolean") {
    const options = column.columnDef.meta?.options ?? [
      { label: "Yes", value: "true" },
      { label: "No", value: "false" },
    ];
    return (
      <Select
        comboboxProps={{ withinPortal: false }}
        label="Value"
        data={options}
        value={typeof filter.value === "string" ? filter.value : null}
        onChange={(value) => onFilterUpdate(filter.filterId!, { value })}
        size="xs"
      />
    );
  }

  if (variant === "multi-select") {
    return (
      <MultiSelect
        comboboxProps={{ withinPortal: false }}
        label="Value"
        data={column.columnDef.meta?.options ?? []}
        value={Array.isArray(filter.value) ? filter.value : []}
        onChange={(value) => onFilterUpdate(filter.filterId!, { value })}
        size="xs"
      />
    );
  }

  if (variant === "date") {
    if (operator === "inRange") {
      const value = Array.isArray(filter.value) ? filter.value : [];
      return (
        <Group grow>
          <TextInput
            label="From"
            type="date"
            value={toDateInputValue(value[0])}
            onChange={(event) =>
              onFilterUpdate(filter.filterId!, {
                value: [event.currentTarget.value ? new Date(event.currentTarget.value).toISOString() : undefined, value[1]],
              })
            }
            size="xs"
          />
          <TextInput
            label="To"
            type="date"
            value={toDateInputValue(value[1])}
            onChange={(event) =>
              onFilterUpdate(filter.filterId!, {
                value: [value[0], event.currentTarget.value ? new Date(event.currentTarget.value).toISOString() : undefined],
              })
            }
            size="xs"
          />
        </Group>
      );
    }

    return (
      <TextInput
        label="Value"
        type="date"
        value={toDateInputValue(filter.value)}
        onChange={(event) =>
          onFilterUpdate(filter.filterId!, {
            value: event.currentTarget.value ? new Date(event.currentTarget.value).toISOString() : null,
          })
        }
        size="xs"
      />
    );
  }

  if (variant === "number") {
    return (
      <TextInput
        label="Value"
        type="number"
        value={typeof filter.value === "number" || typeof filter.value === "string" ? filter.value : ""}
        onChange={(event) =>
          onFilterUpdate(filter.filterId!, {
            value: event.currentTarget.value === "" ? null : Number(event.currentTarget.value),
          })
        }
        size="xs"
      />
    );
  }

  return (
    <TextInput
      label="Value"
      value={typeof filter.value === "string" ? filter.value : ""}
      onChange={(event) => onFilterUpdate(filter.filterId!, { value: event.currentTarget.value || null })}
      size="xs"
    />
  );
}

export function AppTableFilterManager<TData extends RowData>({ table, simpleHeader }: AppTableControlProps<TData> & { simpleHeader?: boolean }) {
  const appTable = asAppTable(table);
  const columnFilters = appTable.state.columnFilters as ExtendedColumnFilter[];
  const filterableColumns = appTable.getAllColumns().filter((column) => column.id !== "select" && column.getCanFilter()) as AppColumn[];
  const fieldOptions = filterableColumns.map((column) => ({ value: column.id, label: getColumnLabel(column) }));

  const updateFilter = (filterId: string, patch: Partial<ExtendedColumnFilter>) => {
    updateColumnFilters(appTable, (current) =>
      current.map((filter) => (filter.filterId === filterId ? { ...filter, ...patch } : filter)),
    );
  };

  const addFilter = () => {
    const [column] = filterableColumns;
    if (!column) return;
    const operators = getFilterOperators(column.columnDef.meta?.variant ?? "text");
    updateColumnFilters(appTable, (current) => [
      ...current,
      {
        id: column.id,
        filterId: makeFilterId(),
        value: null,
        operator: operators[0].value,
        joinOperator: current[0]?.joinOperator ?? "and",
      },
    ]);
  };

  return (
        <Stack w="100%" gap="xs" onMouseDown={(event) => event.stopPropagation()} onClick={(event) => event.stopPropagation()}>
          <Group wrap="nowrap" w="100%" justify="space-between">
            {!simpleHeader && <Text fw={600} c="dimmed" size="xs">Filters</Text>}
            <Button 
                size="compact-xs" 
                onClick={addFilter} 
                disabled={filterableColumns.length === 0}
                leftSection={<IconFilterPlus size={16} />}
            >Add filter</Button>
            <div className="grow" />
            {!simpleHeader && <ResetToDefaultButton
              onClick={() => appTable.resetColumnFilters()}
              disabled={columnFilters.length === 0}
            />}
          </Group>
          {columnFilters.length === 0 ? (
            <EmptyState
              icon={<IconFilter />}
              title="No filters active"
              description="Add filters to narrow down the data in the table."
              size="xs"
              mt="md"
            />
          ) : null}
          {columnFilters.map((filter, index) => {
            const column = appTable.getColumn(filter.id) as AppColumn | undefined;
            if (!column || !filter.filterId) return null;
            const variant = column.columnDef.meta?.variant ?? "text";
            const operators = getFilterOperators(variant);

            return (
              <Group key={filter.filterId} align="flex-end" wrap="nowrap" gap={5}>
                {index === 0 ? (
                  <TextInput
                    w={60}
                    size="xs"
                    readOnly
                    value="Where"
                    variant="filled"
                  />
                ) : index === 1 ? (
                  <Select
                    comboboxProps={{ withinPortal: false }}
                    data={[{ value: "and", label: "and" }, { value: "or", label: "or" }]}
                    value={filter.joinOperator ?? "and"}
                    onChange={(joinOperator) => {
                      if (!joinOperator) return;
                      updateColumnFilters(appTable, (current) => current.map((item) => ({ ...item, joinOperator: joinOperator as "and" | "or" })));
                    }}
                    w={60}
                    size="xs"
                    rightSection={null}
                  />
                ) : (
                  <Text w={70} pb={8} size="xs">{filter.joinOperator ?? "and"}</Text>
                )}
                <Select
                  comboboxProps={{ withinPortal: false }}
                  label="Field"
                  searchable
                  data={fieldOptions}
                  value={column.id}
                  onChange={(nextColumnId) => {
                    const nextColumn = nextColumnId ? appTable.getColumn(nextColumnId) as AppColumn | undefined : undefined;
                    if (!nextColumn) return;
                    updateFilter(filter.filterId!, {
                      id: nextColumn.id,
                      operator: getFilterOperators(nextColumn.columnDef.meta?.variant ?? "text")[0].value,
                      value: null,
                    });
                  }}
                  size="xs"
                  w={90}
                />
                <Select
                  comboboxProps={{ withinPortal: false }}
                  label="Operator"
                  data={operators.map((operator) => ({ value: operator.value, label: operator.label }))}
                  value={filter.operator ?? operators[0].value}
                  onChange={(operator) => updateFilter(filter.filterId!, { operator: operator as FilterOperator, value: null })}
                  w={90}
                  size="xs"
                />
                <Box style={{ flex: 1 }}>
                  <FilterValueInput column={column} filter={filter} onFilterUpdate={updateFilter} />
                </Box>
                <Tooltip label="Remove filter" position="bottom" withArrow>
                    <ActionIcon
                        variant="subtle"
                        aria-label="Remove filter"
                        onClick={() => updateColumnFilters(appTable, (current) => current.filter((item) => item.filterId !== filter.filterId))}
                        >
                        <IconX size={16} />
                    </ActionIcon>
                </Tooltip>
              </Group>
            );
          })}
        </Stack>
  );
}

export function AppTableFilterButton<TData extends RowData>({ table }: AppTableControlProps<TData>) {
  const appTable = asAppTable(table);
  const [opened, setOpened] = React.useState(false);
  const columnFilters = appTable.state.columnFilters as ExtendedColumnFilter[];

  return (
    <Popover opened={opened} onChange={setOpened} width={760} shadow="md" position="bottom-end" closeOnEscape={false}>
      <Popover.Target>
        <Button
          variant="light"
          size="xs"
          leftSection={<IconFilter size={16} />}
          rightSection={columnFilters.length ? <Badge size="sm">{columnFilters.length}</Badge> : null}
          onClick={() => setOpened((value) => !value)}
        >
          Filters
        </Button>
      </Popover.Target>
      <Popover.Dropdown w={520}>
        <AppTableFilterManager table={table} />
      </Popover.Dropdown>
    </Popover>
  );
}

export function AppTableSortButton<TData extends RowData>({ table }: AppTableControlProps<TData>) {
  const appTable = asAppTable(table);
  const sortableColumns = appTable.getAllColumns().filter((column) => column.id !== "select" && column.getCanSort()) as AppColumn[];
  const columns: SortColumnOption[] = sortableColumns.map((column) => ({
    value: column.id,
    label: getColumnLabel(column),
    valueType: getSortColumnValueType(column),
  }));

  return (
    <SortManager
      sorting={appTable.state.sorting as any}
      setSorting={((updater) => appTable.setSorting(updater as any)) as any}
      columns={columns}
      onReset={() => appTable.resetSorting()}
    />
  );
}

export function AppTableGroupingButton<TData extends RowData>({ table }: AppTableControlProps<TData>) {
  const appTable = asAppTable(table);
  const [opened, setOpened] = React.useState(false);
  const grouping = appTable.state.grouping ?? [];
  const groupableColumns = appTable.getAllColumns()
    .filter((column) => column.id !== "select" && column.getCanGroup()) as AppColumn[];

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end" shadow="md">
      <Popover.Target>
        <Button
          variant="light"
          size="xs"
          leftSection={<IconSitemap size={16} />}
          rightSection={grouping.length ? <Badge size="sm">{grouping.length}</Badge> : null}
          onClick={() => setOpened((value) => !value)}
        >
          Groups
        </Button>
      </Popover.Target>
      <Popover.Dropdown w={320}>
        <Stack gap="sm">
          <Group justify="space-between" wrap="nowrap">
            <Text fw={600} c="dimmed" size="xs">Groups</Text>
            <ResetToDefaultButton
              onClick={() => appTable.resetGrouping()}
              disabled={grouping.length === 0}
            />
          </Group>
          <Stack gap={4}>
            {groupableColumns.length === 0 ? (
              <Text c="dimmed" size="sm">No groupable columns</Text>
            ) : groupableColumns.map((column) => (
              <Checkbox
                key={column.id}
                checked={column.getIsGrouped()}
                label={getColumnLabel(column)}
                onChange={column.getToggleGroupingHandler()}
                size="xs"
              />
            ))}
          </Stack>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

export function AppTableViewButton<TData extends RowData>({ table }: AppTableControlProps<TData>) {
  const appTable = asAppTable(table);
  const [opened, setOpened] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const columnOrder = appTable.state.columnOrder ?? [];
  const columns = (appTable.getAllColumns() as AppColumn[])
    .filter((column) => column.id !== "select" && column.getCanHide())
    .sort((a, b) => {
      const aIndex = columnOrder.indexOf(a.id);
      const bIndex = columnOrder.indexOf(b.id);
      return (aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex) - (bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex);
    })
    .filter((column) => getColumnLabel(column).toLowerCase().includes(query.toLowerCase()));

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const visibleColumnIds = columns.map((column) => column.id);
    const existingOrder = columnOrder.length ? columnOrder : appTable.getAllLeafColumns().map((column) => column.id);
    const oldIndex = visibleColumnIds.indexOf(String(active.id));
    const newIndex = visibleColumnIds.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    const reorderedVisible = arrayMove(visibleColumnIds, oldIndex, newIndex);
    appTable.setColumnOrder(existingOrder.map((id) => reorderedVisible.includes(id) ? reorderedVisible.shift()! : id));
  };

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end" shadow="md">
      <Popover.Target>
        <Button variant="light" size="xs" leftSection={<IconColumns size={16} />} onClick={() => setOpened((value) => !value)}>
          Columns
        </Button>
      </Popover.Target>
      <Popover.Dropdown w={360}>
        <Stack gap={5}>
            <Group justify="space-between" wrap="nowrap">
                <Text fw={600} c={'dimmed'} size="xs">Columns</Text>
                <ResetToDefaultButton
                    onClick={() => appTable.resetColumnVisibility()}
                />
            </Group>
            <Stack gap={5}>
                <TextInput size="xs" placeholder="Search..." value={query} onChange={(event) => setQuery(event.currentTarget.value)} />
                <Group wrap="nowrap" gap={5}>
                    <Button
                      fullWidth
                      size="xs"
                      variant="outline"
                      onClick={() => columns.forEach((column) => column.toggleVisibility(false))}
                      disabled={!columns.some((column) => column.getIsVisible())}
                    >
                        Hide all
                    </Button>
                    <Button
                      fullWidth
                      size="xs"
                      variant="outline"
                      onClick={() => columns.forEach((column) => column.toggleVisibility(true))}
                      disabled={columns.every((column) => column.getIsVisible())}
                    >
                        Show all
                    </Button>
                </Group>
            </Stack>
            <Divider my={7}/>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={columns.map((column) => column.id)} strategy={verticalListSortingStrategy}>
                <Stack gap={4}>
                    {columns.map((column) => (
                    <SortableFrame key={column.id} id={column.id}>
                        <Group justify="space-between" wrap="nowrap">
                        <Checkbox
                            checked={column.getIsVisible()}
                            label={getColumnLabel(column)}
                            onChange={() => column.toggleVisibility(!column.getIsVisible())}
                            size="xs"
                        />
                        <IconGripVertical size={16} opacity={0.45} />
                        </Group>
                    </SortableFrame>
                    ))}
                </Stack>
                </SortableContext>
            </DndContext>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

function exportTableToExcel<TData extends RowData>(table: ReactTable<AppTableFeatures, TData, any>, fileName = "table-export.xlsx") {
  const appTable = asAppTable(table);
  const columns = appTable.getVisibleLeafColumns();
  const rows = appTable.getRowModel().rows;
  
  // Create CSV data
  const headers = columns.map((col) => {
    const meta = col.columnDef.meta;
    return meta?.label ?? (typeof col.columnDef.header === "string" ? col.columnDef.header : col.id);
  });
  
  const csvData = [
    headers.join(","),
    ...rows.map((row) =>
      columns.map((col) => {
        const cell = row.getVisibleCells().find((c) => c.column.id === col.id);
        if (!cell) return "";
        const value = cell.getValue();
        // Escape quotes and wrap in quotes if contains comma or newline
        const stringValue = String(value ?? "");
        return stringValue.includes(",") || stringValue.includes("\n") || stringValue.includes('"')
          ? `"${stringValue.replace(/"/g, '""')}"`
          : stringValue;
      }).join(",")
    ),
  ].join("\n");
  
  // Create blob and download
  const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", fileName);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function AppTableMoreOptionsButton<TData extends RowData>({
  table,
  striped = true,
  onStripedChange,
  compact = false,
  onCompactChange,
  onResetToDefault,
  onExportToExcel,
}: AppTableControlProps<TData> & Pick<AppTableToolbarControlsOptions, "striped" | "onStripedChange" | "compact" | "onCompactChange" | "onResetToDefault" | "onExportToExcel">) {
  const [opened, setOpened] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      if (onExportToExcel) {
        await onExportToExcel();
      } else {
        exportTableToExcel(table);
      }
    } finally {
      setExporting(false);
    }
  };

  const handleResetToDefault = () => {
    asAppTable(table).resetColumnFilters();
    asAppTable(table).resetSorting();
    asAppTable(table).resetColumnVisibility();
    asAppTable(table).resetColumnOrder();
    asAppTable(table).resetGrouping();
    onResetToDefault?.();
  };

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end" shadow="md">
      <Popover.Target>
        <Tooltip label="More table options" position="bottom" withArrow>
            <ActionIcon variant="light" size="input-xs" onClick={() => setOpened((value) => !value)}>
            <IconDots size={16} />
            </ActionIcon>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <Stack gap={0} style={{ minWidth: 200 }}>
            <Group justify="space-between" wrap="nowrap" p={'sm'} py={'xs'}>
                <Text fw={600} c="dimmed" size="xs">Table</Text>
                <ResetToDefaultButton onClick={handleResetToDefault} size="xs" />
            </Group>
            <Divider my={0} />
          
          <Stack p={'sm'} gap={'lg'}>
                <Stack gap={5}>
                    <Text fw={400} c="dimmed" size="xs">Appearance</Text>
                    <Group justify="space-between" wrap="nowrap">
                        <Text size="sm">Striped rows</Text>
                        <Switch
                        checked={striped === true || striped === "odd" || striped === "even"}
                        onChange={(event) => onStripedChange?.(event.currentTarget.checked)}
                        size="sm"
                        />
                    </Group>
                    <Group justify="space-between" wrap="nowrap">
                        <Text size="sm">Compact rows</Text>
                        <Switch
                        checked={compact}
                        onChange={(event) => onCompactChange?.(event.currentTarget.checked)}
                        size="sm"
                        />
                    </Group>
                </Stack>
            
                {/* <Stack gap={5}>
                    <Text fw={400} c="dimmed" size="xs">Actions</Text>
                    <Button
                        fullWidth
                        size="xs"
                        variant="outline"
                        leftSection={<IconDownload size={14} />}
                        onClick={handleExport}
                        loading={exporting}
                    >
                        Export to Excel
                    </Button>
                </Stack> */}
            </Stack>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

export function AppTableToolbarControls<TData extends RowData>({
  table,
  filter = true,
  grouping = true,
  sort = true,
  view = true,
  moreOptions = true,
  gap = "xs",
  striped = true,
  onStripedChange,
  compact = false,
  onCompactChange,
  onResetToDefault,
  onExportToExcel,
}: AppTableControlProps<TData> & AppTableToolbarControlsOptions) {
  return (
    <Group gap={gap} wrap="nowrap">
      {filter ? <AppTableFilterButton table={table} /> : null}
      {grouping ? <AppTableGroupingButton table={table} /> : null}
      {sort ? <AppTableSortButton table={table} /> : null}
      {view ? <AppTableViewButton table={table} /> : null}
      {moreOptions ? (
        <AppTableMoreOptionsButton
          table={table}
          striped={striped}
          onStripedChange={onStripedChange}
          compact={compact}
          onCompactChange={onCompactChange}
          onResetToDefault={onResetToDefault}
          onExportToExcel={onExportToExcel}
        />
      ) : null}
    </Group>
  );
}
